// Powerlete mobile quick-add bottom-sheet component.
// Extracted verbatim from the inline <script> in snippets/powerlete-quick-add-sheet.liquid
// (loaded there as a deferred module) so the ~7KB is a single cached asset instead of inline in
// every HTML doc. No Liquid was interpolated; strings/routes come from the element's data-* attrs
// and window.Theme/Shopify.routes at runtime. Behaviour is unchanged from the inline version.
  if (!customElements.get('powerlete-quick-add-sheet')) {
    customElements.define('powerlete-quick-add-sheet', class extends HTMLElement {
      connectedCallback() {
        this.overlay = this.querySelector('[data-pqa-overlay]');
        this.panel = this.querySelector('[data-pqa-panel]');
        this.body = this.querySelector('[data-pqa-body]');
        this.closeBtn = this.querySelector('[data-pqa-close]');
        this.sectionId = this.dataset.sectionId;
        this.lastFocus = null;
        this.controller = null;

        this.overlay?.addEventListener('click', () => this.close());
        this.closeBtn?.addEventListener('click', () => this.close());
        this.addEventListener('keydown', (e) => this.onKeydown(e));

        // Delegated interactions on the dynamically-injected content. The ATC tap is handled
        // here too: while a size is required (or in edit mode) the ATC is a disabled CTA with
        // pointer-events:none, so the tap falls through to the wrap and reaches onBodyClick.
        this.body?.addEventListener('click', (e) => this.onBodyClick(e));

        // Close on successful add (cart drawer takes over). Immediate: the drawer's own open
        // animation is about to be the only thing visible, see close()'s doc comment.
        document.addEventListener('cart:update', () => { if (this.isOpen) this.close({ immediate: true }); });
        document.addEventListener('cart-add', () => { if (this.isOpen) this.close({ immediate: true }); });

        // Expose a tiny global API for card openers
        window.powerleteQuickAdd = this;

        // Edit-mode state (set by openEdit; cleared by open/close)
        this.editLineKey = null;
        this.editVariantId = null;
        this.editQuantity = 1;
        this.reopenDrawer = null;
        this.inertedEls = [];

        // Re-entrancy guard. close() defers hiding the sheet to a transitionend + timeout
        // fallback. If open() runs again before that fires, the stale callback would set
        // [hidden] on the freshly-reopened sheet while the body scroll lock (added by the new
        // open) stays on, leaving the page invisibly scroll-locked (Escape cannot recover it
        // because the element is display:none). `gen` invalidates any close callback whose open
        // has since been superseded; cancelPendingClose() aborts the pending callback outright.
        this.gen = 0;
        this.pendingCloseTimer = null;
        this.pendingCloseDone = null;

        // Delegated: cart line "Edit" buttons open the sheet in edit mode.
        document.addEventListener('click', (e) => {
          const editBtn = e.target.closest('[data-pw-edit]');
          if (!editBtn) return;
          e.preventDefault();
          // The cart drawer is a modal <dialog> in the browser top layer, so this z-index sheet
          // would open BEHIND it (inert) and look like nothing happened. Close the drawer first
          // (mirrors the card opener, powerlete-quick-add-opener) and remember it so we can
          // reopen it — showing the updated cart — when the sheet closes.
          const drawer = editBtn.closest('cart-drawer-component') || editBtn.closest('dialog-component');
          if (drawer && typeof drawer.closeDialog === 'function') {
            this.reopenDrawer = drawer;
            drawer.closeDialog();
          } else {
            this.reopenDrawer = null;
            editBtn.closest('dialog')?.close();
          }
          this.openEdit(editBtn.dataset.handle, editBtn.dataset.lineKey, editBtn.dataset.variantId, editBtn.dataset.quantity);
        });
      }

      get isOpen() { return !this.hasAttribute('hidden'); }

      async open(handle, listName) {
        if (!handle) return;
        // Abort any in-flight close so its deferred done() can't hide this open or strand the lock.
        this.cancelPendingClose();
        this.gen++;
        this.editLineKey = null;
        this.editVariantId = null;
        this.editQuantity = 1;
        this.reopenDrawer = null;
        this.removeAttribute('data-pqa-edit');
        this.dataset.listName = listName || '';
        this.lastFocus = document.activeElement;
        this.removeAttribute('hidden');
        // Apply the scroll lock + background inert in the SAME frame as is-open, so the page is
        // never locked while the sheet is still invisible (panel off-screen / overlay transparent
        // until the transition runs). If this frame never fires, nothing is locked, so the page
        // can never strand scroll-locked-but-invisible (recoverable only by Escape/reload).
        // Snapshot the generation: if a newer open()/openEdit() supersedes this one (or a close
        // already hid the sheet) before the frame fires, bail — otherwise setBackgroundInert()
        // runs twice, the second run resets inertedEls and skips the still-inert children, and
        // they can never be cleared (header/main permanently inert until reload).
        const g = this.gen;
        requestAnimationFrame(() => {
          if (g !== this.gen || this.hasAttribute('hidden')) return;
          document.body.classList.add('pqa-sheet-open');
          this.setBackgroundInert();
          this.classList.add('is-open');
        });
        await this.load(handle);
        this.trapFocus();
      }

      /**
       * Open the sheet in EDIT mode for an existing cart line.
       * Swapping a variant = remove the old line key + add the chosen variant
       * (Shopify's /cart/change cannot change a line's variant id).
       */
      async openEdit(handle, lineKey, variantId, quantity) {
        if (!handle || !lineKey) return;
        // Abort any in-flight close so its deferred done() can't hide this open or strand the lock.
        this.cancelPendingClose();
        this.gen++;
        this.editLineKey = lineKey;
        this.editVariantId = variantId || null;
        this.editQuantity = Math.max(1, parseInt(quantity, 10) || 1);
        this.setAttribute('data-pqa-edit', '');
        this.dataset.listName = 'Cart Edit';
        this.lastFocus = document.activeElement;
        this.removeAttribute('hidden');
        // Same as open(): lock + inert + is-open atomically in one frame, never lock-before-visible.
        // Same generation guard as open() — a superseding open/close before the frame fires must
        // not double-run setBackgroundInert() and strand inert on the body children.
        const g = this.gen;
        requestAnimationFrame(() => {
          if (g !== this.gen || this.hasAttribute('hidden')) return;
          document.body.classList.add('pqa-sheet-open');
          this.setBackgroundInert();
          this.classList.add('is-open');
        });
        await this.load(handle);
        this.applyEditMode(variantId);
        this.trapFocus();
      }

      // Relabel the sheet for edit + preselect the current variant + route ATC to update.
      applyEditMode(variantId) {
        const heading = this.querySelector('.pqa-sheet__heading');
        if (heading) heading.textContent = this.dataset.editTitle || 'Edit product';
        const atc = this.body.querySelector('.pqa-sheet__atc');
        if (atc) {
          const label = atc.querySelector('.add-to-cart-text, .button-text') || atc;
          label.textContent = this.dataset.editCta || 'Update item';
          // Edit ATC is a styled-but-disabled CTA; its tap falls through to onBodyClick, which
          // routes to updateLine() instead of Horizon's add (prevents a double-add).
          atc.classList.add('pqa-sheet__atc--cta');
          atc.setAttribute('disabled', '');
        }
        // Preselect the current variant's size button if present.
        if (variantId) {
          const sizeBtn = this.body.querySelector(`[data-pqa-size][data-variant-id="${variantId}"]`);
          if (sizeBtn) this.selectSize(sizeBtn);
          const input = this.body.querySelector('[data-pqa-variant-input]');
          if (input && !sizeBtn) input.value = variantId;
        }
      }

      /**
       * @param {{ immediate?: boolean }} [opts] - `immediate: true` skips the panel's own
       *   slide-down transition and hides synchronously. Use this when something else is about
       *   to paint over the whole viewport anyway (the cart drawer auto-opening on a successful
       *   add): the drawer is a native <dialog> promoted to the browser's top layer the instant
       *   showModal() runs, so it covers this sheet within one frame regardless of z-index —
       *   the panel's 0.32s transition would never actually be visible, just delay the handoff
       *   and hold the shopper looking at a frozen sheet (2026-07-08 stutter review). Manual
       *   dismiss (X / overlay / Escape) has nothing covering it, so that path keeps the animated
       *   close.
       */
      close(opts) {
        const immediate = !!opts?.immediate;
        this.classList.remove('is-open');
        document.body.classList.remove('pqa-sheet-open');
        // Restore the background synchronously (not in the deferred done() below): it must run
        // even if the transition fallback races, and BEFORE done() reopens the cart drawer, so a
        // reopened drawer is never restored into an inert subtree.
        this.clearBackgroundInert();
        // If we closed the cart drawer to show this sheet (edit mode), reopen it on close so the
        // shopper returns to the now-updated cart. Capture + clear first so a double close()
        // (the cart:update listener + updateLine's finally) cannot reopen it twice.
        const drawerToReopen = this.reopenDrawer;
        this.reopenDrawer = null;
        // Snapshot the open generation. If open()/openEdit() runs again before this close
        // finishes animating, gen advances and done() must NOT hide the now-live sheet.
        const myGen = this.gen;
        const done = () => {
          if (this.gen !== myGen) return;
          this.cancelPendingClose();
          this.setAttribute('hidden', '');
          // Belt and braces: neither the scroll lock nor the background inert may outlive a hidden
          // sheet. clearBackgroundInert() here catches the race where a rAF from open() ran AFTER
          // close()'s synchronous clear (inertedEls was empty then, populated by the later rAF).
          // Runs before the drawer reopen below, so a reopened drawer is never left in an inert
          // subtree. Idempotent, so the synchronous clear in close() is harmless to repeat.
          document.body.classList.remove('pqa-sheet-open');
          this.clearBackgroundInert();
          if (drawerToReopen && typeof drawerToReopen.open === 'function') {
            const dlg = drawerToReopen.querySelector('dialog');
            if (!dlg || !dlg.open) {
              // Reopening the cart drawer calls native showModal(), which auto-focuses the drawer's
              // first focusable control (the close X). On iOS the global *:focus-visible rule then
              // paints that as a stray box on the reopened cart line's title (Matt, on-device).
              // Once the drawer has opened, move focus to the dialog container (tabindex="-1") so the
              // box never appears: programmatic focus on a non-interactive container is not
              // :focus-visible. Mirrors the verified quick-add panel + search-modal fixes
              // (DECISIONS.md 2026-07-12 / plain-X). Keyboard Tab still lands on the close button next.
              // dialog:open is dispatched right AFTER showModal() (assets/dialog.js showDialog), so this
              // reliably overrides the browser auto-focus instead of racing it.
              drawerToReopen.addEventListener(
                'dialog:open',
                () => {
                  const reopened = drawerToReopen.querySelector('dialog');
                  if (reopened && reopened.open) reopened.focus({ preventScroll: true });
                },
                { once: true }
              );
              drawerToReopen.open();
            }
          } else if (this.lastFocus && this.lastFocus.focus) {
            this.lastFocus.focus();
          }
        };
        this.cancelPendingClose();
        if (immediate) { done(); return; }
        // Replace any earlier pending close, then schedule this one (transitionend + timeout
        // fallback). cancelPendingClose() lets a subsequent open() abort it cleanly.
        this.pendingCloseDone = done;
        this.panel.addEventListener('transitionend', done, { once: true });
        this.pendingCloseTimer = setTimeout(() => { if (this.isOpen) done(); }, 400);
      }

      // Abort a deferred close() (its transitionend listener + timeout fallback) so it can never
      // fire after a fresh open() and leave the page hidden-but-scroll-locked.
      cancelPendingClose() {
        if (this.pendingCloseTimer) { clearTimeout(this.pendingCloseTimer); this.pendingCloseTimer = null; }
        if (this.pendingCloseDone) { this.panel.removeEventListener('transitionend', this.pendingCloseDone); this.pendingCloseDone = null; }
      }

      async load(handle) {
        this.body.setAttribute('aria-busy', 'true');
        this.controller?.abort();
        this.controller = new AbortController();
        const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const url = `${root}products/${handle}?section_id=${this.sectionId}`;
        try {
          const res = await fetch(url, { signal: this.controller.signal });
          if (!res.ok) throw new Error('fetch failed ' + res.status);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('[data-pqa-inner]');
          if (inner) {
            this.body.innerHTML = '';
            this.body.appendChild(inner);
            this.preselectSingleSize();
            this.initMediaNav();
            this.syncAtcState();
          }
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('[powerlete-quick-add]', err);
            this.body.innerHTML = '<p class="pqa-sheet__error" role="alert">' + (this.dataset.fetchError || 'Unable to load product. Please try again.') + '</p>';
          }
        } finally {
          this.body.setAttribute('aria-busy', 'false');
        }
      }

      onBodyClick(e) {
        const prev = e.target.closest('[data-pqa-prev]');
        if (prev) { e.preventDefault(); this.scrollMedia(-1); return; }
        const next = e.target.closest('[data-pqa-next]');
        if (next) { e.preventDefault(); this.scrollMedia(1); return; }

        const swatch = e.target.closest('[data-pqa-swatch]');
        if (swatch) {
          e.preventDefault();
          if (swatch.classList.contains('is-active')) return;
          this.load(swatch.dataset.handle).then(() => this.trapFocus());
          return;
        }
        const size = e.target.closest('[data-pqa-size]');
        if (size) {
          e.preventDefault();
          this.selectSize(size);
          return;
        }

        // Fully sold-out product: "Notify me" opens the global card notify sheet.
        const bisBtn = e.target.closest('[data-pqa-bis-open]');
        if (bisBtn) {
          e.preventDefault();
          this.openNotify(bisBtn);
          return;
        }

        // ATC tap (pointer or keyboard — a real button activation bubbles here):
        // - Add mode, buyable size: the ATC is an enabled submit button, so the tap lands on it
        //   and Horizon adds; this branch is a no-op.
        // - Add mode, size still required: the ATC is a disabled CTA (pointer-events:none), so the
        //   tap falls through here and we show the size nudge.
        // - Edit mode: the ATC is an enabled <button type=button> (buyable) whose click bubbles
        //   here to route to updateLine(), or a disabled CTA (awaiting size / sold-out target).
        const atcWrap = e.target.closest('.pqa-sheet__atc-wrap');
        if (atcWrap) {
          const sizeButtons = this.body.querySelectorAll('[data-pqa-size]');
          const selected = this.body.querySelector('[data-pqa-size].is-selected');
          const noSizeChosen = sizeButtons.length > 0 && !selected;
          if (this.editLineKey) {
            // Edit mode but the (possibly just colour-swapped) sibling has no size chosen yet:
            // prompt rather than updating the line to the default variant.
            if (noSizeChosen) { this.showSizeRequired(); return; }
            // Never commit a sold-out target variant (the button is disabled for it, but ignore
            // stray taps on the wrap padding too). The status line already shows it as sold out;
            // committing it would remove the original line and fail to add — losing the item.
            if (selected) {
              const preZone = this.body.querySelector('[data-pqa-status]')?.dataset.preOrderZone === 'true';
              const policy = (selected.dataset.inventoryPolicy || '').toLowerCase();
              const buyable = selected.dataset.available === 'true' || (policy === 'continue' && preZone);
              if (!buyable) return;
            }
            const input = this.body.querySelector('[data-pqa-variant-input]');
            if (input && input.value) this.updateLine(input.value);
            return;
          }
          if (noSizeChosen) this.showSizeRequired();
        }
      }

      scrollMedia(direction) {
        const strip = this.body.querySelector('[data-pqa-media]');
        if (!strip) return;
        const img = strip.querySelector('img');
        if (!img) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        strip.scrollBy({ left: direction * (img.offsetWidth + 6), behavior: reduce ? 'auto' : 'smooth' });
        setTimeout(() => this.updateNav(), 350);
      }

      updateNav() {
        const strip = this.body.querySelector('[data-pqa-media]');
        const prev = this.body.querySelector('[data-pqa-prev]');
        const next = this.body.querySelector('[data-pqa-next]');
        if (!strip || !prev || !next) return;
        const atStart = strip.scrollLeft < 4;
        const atEnd = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 4;
        prev.disabled = atStart;
        next.disabled = atEnd;
      }

      initMediaNav() {
        const strip = this.body.querySelector('[data-pqa-media]');
        if (!strip) return;
        this.updateNav();
        strip.addEventListener('scroll', () => this.updateNav(), { passive: true });
      }

      /**
       * Fully sold-out product: open the global card notify sheet (window.powerleteQuickAddNotify),
       * reusing the PDP's Klaviyo back-in-stock subscribe. Build the Size options from the fetched
       * size buttons (or a single entry for a one-variant product) and preselect the chosen size, or
       * the first variant if none is chosen (fully-OOS products auto-select nothing). Close THIS
       * sheet first (mirrors the drawer->sheet edit flow) so its background-inert is cleared and the
       * notify sheet is interactive; immediate close avoids a visible two-sheet overlap.
       */
      openNotify(btn) {
        const notify = window.powerleteQuickAddNotify;
        if (!notify || typeof notify.open !== 'function') return;
        const sizeButtons = [...this.body.querySelectorAll('[data-pqa-size]')];
        let variants;
        if (sizeButtons.length) {
          variants = sizeButtons.map((s) => ({
            id: s.dataset.variantId,
            title: (s.textContent || '').trim(),
            available: s.dataset.available === 'true',
          }));
        } else {
          variants = [{ id: btn.dataset.variant, title: btn.dataset.variantTitle || '', available: false }];
        }
        const selected = this.body.querySelector('[data-pqa-size].is-selected');
        const variantId = (selected && selected.dataset.variantId) || btn.dataset.variant || (variants[0] && variants[0].id);
        const inner = this.body.querySelector('[data-pqa-inner]');
        const productId = inner && inner.dataset.productId;
        const handle = inner && inner.dataset.productHandle;
        // Null lastFocus so this sheet's close() doesn't steal focus back from the notify sheet
        // (there is no drawer to reopen in this path).
        this.lastFocus = null;
        this.close({ immediate: true });
        notify.open(variantId, { variants, productId, handle });
      }

      // Reveal the "choose a size" nudge. The nudge text is static in the DOM, so a visibility
      // toggle alone is not reliably announced by assistive tech; insert the message into the
      // live region so it is announced (assertive).
      showSizeRequired() {
        const choose = this.body.querySelector('[data-pqa-choose]');
        if (choose) choose.hidden = false;
        const live = this.body.querySelector('[data-pqa-live]');
        if (live) { live.textContent = ''; live.textContent = (choose && choose.textContent.trim()) || ''; }
      }

      /**
       * Swap a cart line to a new variant, preserving the line quantity.
       *
       * Order matters: ADD the new variant FIRST, then remove the old line. If the add fails
       * (e.g. the target sold out between load and commit) the original line is left untouched,
       * so the customer never loses the item. The remove requests the cart section so the drawer
       * morphs in a single pass, and the full cart is dispatched so the count + free-ship bar
       * update without their own extra fetch.
       * @param {string} newVariantId
       */
      async updateLine(newVariantId) {
        const lineKey = this.editLineKey;
        if (!lineKey || !newVariantId) return;
        const qty = this.editQuantity || 1;
        // Re-selecting the same variant is a no-op.
        if (this.editVariantId && String(newVariantId) === String(this.editVariantId)) { this.close(); return; }

        const atc = this.body.querySelector('.pqa-sheet__atc');
        atc?.setAttribute('disabled', '');
        const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const cartItems = document.querySelector('cart-items-component[data-drawer]') || document.querySelector('cart-items-component');
        const sectionId = cartItems?.dataset.sectionId || '';

        try {
          // 1) Add the chosen variant first, at the original line quantity. If this fails, the
          //    original line is still in the cart — leave the sheet open so the customer can retry.
          const addRes = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(newVariantId), quantity: qty }] }),
          });
          if (!addRes.ok) { atc?.removeAttribute('disabled'); return; }

          // 2) Remove the old line, requesting the cart section so the drawer morphs in one pass.
          const changeBody = { id: lineKey, quantity: 0 };
          if (sectionId) { changeBody.sections = sectionId; changeBody.sections_url = window.location.pathname; }
          const changeRes = await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(changeBody),
          });
          const cart = changeRes.ok ? await changeRes.json() : null;

          // 3) Refresh the drawer + free-ship bar + upsell through the normal cart path. The full
          //    cart (item_count/total_price) + section HTML avoid extra round-trips.
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'powerlete-quick-add-edit',
              data: { source: 'powerlete-quick-add-edit', sections: (cart && cart.sections) || {} },
            },
          }));
        } catch (err) {
          console.error('[powerlete-quick-add edit]', err);
        } finally {
          this.editLineKey = null;
          this.editVariantId = null;
          this.editQuantity = 1;
          atc?.removeAttribute('disabled');
          this.close();
        }
      }

      preselectSingleSize() {
        const sizes = this.body.querySelectorAll('[data-pqa-size]');
        const available = [...sizes].filter((s) => s.dataset.available === 'true');
        if (sizes.length === 0) return;          // single-variant product: ATC ready
        if (available.length === 1) this.selectSize(available[0]);
      }

      /**
       * Keep the ATC's edit + a11y state correct after a (re)load, incl. a colour swap.
       * Edit mode: if a size is already selected (e.g. a single-available size auto-preselected
       * after a colour swap), mirror updateAtc so the Update button is an enabled, keyboard-
       * committable <button type=button> when buyable; otherwise show the awaiting-size CTA whose
       * tap falls through to showSizeRequired(). Add mode: describe the disabled CTA for AT.
       */
      syncAtcState() {
        const atc = this.body.querySelector('.pqa-sheet__atc');
        if (!atc) return;
        if (this.editLineKey) {
          const selected = this.body.querySelector('[data-pqa-size].is-selected');
          if (selected) {
            this.updateAtc(selected);
          } else {
            atc.classList.add('pqa-sheet__atc--cta');
            atc.setAttribute('disabled', '');
            atc.removeAttribute('aria-describedby');
          }
          return;
        }
        const sizeButtons = this.body.querySelectorAll('[data-pqa-size]');
        if (sizeButtons.length > 0 && !this.body.querySelector('[data-pqa-size].is-selected')) {
          atc.setAttribute('aria-describedby', 'pqa-size-hint');
        } else {
          atc.removeAttribute('aria-describedby');
        }
      }

      selectSize(btn) {
        const sizes = this.body.querySelectorAll('[data-pqa-size]');
        sizes.forEach((s) => { s.classList.remove('is-selected'); s.setAttribute('aria-pressed', 'false'); });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');

        // update hidden variant id
        const input = this.body.querySelector('[data-pqa-variant-input]');
        if (input) input.value = btn.dataset.variantId;

        // hide the "select size" nudge
        const choose = this.body.querySelector('[data-pqa-choose]');
        if (choose) choose.hidden = true;

        this.updateStatus(btn);
        this.updateAtc(btn);
      }

      updateStatus(btn) {
        const wrap = this.body.querySelector('[data-pqa-status]');
        if (!wrap) return;
        const preZone = wrap.dataset.preOrderZone === 'true';
        const low = parseInt(wrap.dataset.lowStock || '5', 10);
        const available = btn.dataset.available === 'true';
        const policy = (btn.dataset.inventoryPolicy || '').toLowerCase();
        const qty = parseInt(btn.dataset.inventoryQty || '0', 10);
        const show = (key) => wrap.querySelectorAll('.pqa-sheet__status-item').forEach((el) => {
          el.classList.toggle('is-shown', el.getAttribute('data-pqa-status-' + key) !== null);
        });
        const pre = this.body.querySelector('[data-pqa-preorder]');
        if (policy === 'continue' && preZone) { show('pre'); if (pre) pre.hidden = false; }
        else if (!available) { show('oos'); if (pre) pre.hidden = true; }
        else if (qty > 0 && qty <= low) { show('low'); if (pre) pre.hidden = true; }
        else { show('in'); if (pre) pre.hidden = true; }
      }

      updateAtc(btn) {
        const atc = this.body.querySelector('.pqa-sheet__atc');
        if (!atc) return;
        // keep price in step with the chosen size. data-price holds `variant.price | money`, whose
        // output is shop money_format ("HTML without currency" in admin) and may legitimately
        // contain markup. innerHTML so any such markup renders instead of showing as literal tags;
        // the value is shop-controlled settings output, never user input.
        const price = this.body.querySelector('[data-pqa-price] .pqa-sheet__price-now');
        if (price && btn.dataset.price) price.innerHTML = btn.dataset.price;
        const preZone = this.body.querySelector('[data-pqa-status]')?.dataset.preOrderZone === 'true';
        const policy = (btn.dataset.inventoryPolicy || '').toLowerCase();
        const buyable = btn.dataset.available === 'true' || (policy === 'continue' && preZone);
        // Edit mode: the ATC is a real, focusable <button type="button"> that routes to updateLine()
        // (it must NEVER submit Horizon's product form, or it would add a line instead of swapping).
        // Buyable size -> enabled, so the swap can be committed by click OR keyboard (the previous
        // disabled CTA was pointer-only — a keyboard user could pick a size but not commit). A
        // sold-out target -> disabled, so it stays non-committable (no data loss).
        if (this.editLineKey) {
          atc.type = 'button';
          atc.classList.remove('pqa-sheet__atc--cta');
          atc.removeAttribute('aria-describedby');
          atc.toggleAttribute('disabled', !buyable);
          return;
        }
        // A size is now chosen, so drop the awaiting-size CTA styling: buyable -> a real enabled
        // button (Horizon adds on tap); unbuyable (OOS) -> stays disabled and reverts to muted.
        atc.classList.remove('pqa-sheet__atc--cta');
        atc.removeAttribute('aria-describedby');
        atc.toggleAttribute('disabled', !buyable);
        const input = this.body.querySelector('[data-pqa-variant-input]');
        if (input) input.toggleAttribute('disabled', !buyable);
      }

      trapFocus() {
        // Focus the dialog panel itself (not the close button) on open: announces the dialog via
        // aria-modal/aria-label and keeps the X plain (no focus-ring box around it). Keyboard
        // users Tab from here into the controls; onKeydown maintains the trap.
        this.panel.focus({ preventScroll: true });
      }

      // This sheet is a role="dialog" <div> (not a native <dialog>), so it has no top layer and
      // the rest of the page stays in the accessibility tree. The Tab trap (onKeydown) does not
      // constrain an AT virtual cursor, so mark every body-level sibling inert while open. Skip
      // anything already inert (e.g. a Horizon <dialog>) so we only restore what we set.
      setBackgroundInert() {
        this.inertedEls = [];
        for (const el of document.body.children) {
          if (el === this || el.inert) continue;
          el.inert = true;
          this.inertedEls.push(el);
        }
      }
      clearBackgroundInert() {
        this.inertedEls.forEach((el) => { el.inert = false; });
        this.inertedEls = [];
      }

      onKeydown(e) {
        if (e.key === 'Escape') { this.close(); return; }
        if (e.key !== 'Tab') return;
        const f = this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }
